import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2, Eye, EyeOff, Check, ArrowRight, ArrowLeft, Gift } from 'lucide-react';
import axios from 'axios';
import { DEFAULT_TIERS } from '../data/pricingData';
import { FeatureMatrix } from '../components/FeatureMatrix';
import { RequestQuoteDialog } from '../components/RequestQuoteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const INDUSTRIES = [
  { value: 'crane', label: 'Crane Hire' },
  { value: 'earthmoving', label: 'Earthmoving' },
  { value: 'haulage', label: 'Haulage' },
  { value: 'concrete_pumping', label: 'Concrete Pumping' },
  { value: 'street_sweeping', label: 'Street Sweeping' },
];

const buildFeatures = (tier) => [
  ...tier.baseFeatures,
  ...(tier.id === 'professional'
    ? []
    : [`Extra User: $${tier.extraUserPrice}/month`, `Extra Operator: $${tier.extraOperatorPrice}/month`]),
];

const FreeTrial = () => {
  const navigate = useNavigate();
  const { loginV2 } = useAuth();
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);

  const [step, setStep] = useState(1); // 1 = choose plan, 2 = details
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null); // tier id string

  // Live pricing
  const [tiers, setTiers] = useState(DEFAULT_TIERS.map(t => ({ ...t, features: buildFeatures(t) })));
  const [tiersLoading, setTiersLoading] = useState(true);
  const [currency, setCurrency] = useState('AUD');
  const [trialPeriodDays, setTrialPeriodDays] = useState(14);
  const [pricingAvailable, setPricingAvailable] = useState(true);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const [form, setForm] = useState({
    companyName: '',
    industryType: '',
    firstName: '',
    lastName: '',
    workEmail: '',
    phone: '',
    password: '',
    termsAccepted: false,
  });
  const [errors, setErrors] = useState({});

  // Force dark mode on this page
  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') setTheme('dark');
    return () => {
      if (previousThemeRef.current !== 'dark') setTheme(previousThemeRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch live tier pricing from Stripe
  useEffect(() => {
    const fetchTierPricing = async () => {
      setTiersLoading(true);
      try {
        const response = await axios.get(`${API_URL}/billing/tiers`);
        const tierData = response.data.tiers || {};
        setCurrency(response.data.currency || 'AUD');
        setTrialPeriodDays(response.data.trial_period_days || 14);
        setPricingAvailable(response.data.pricing_available !== false);

        const dynamicTiers = DEFAULT_TIERS.map((defaultTier) => {
          const stripeTier = tierData[defaultTier.id];
          if (stripeTier) {
            const merged = {
              ...defaultTier,
              price: stripeTier.base_price || defaultTier.price,
              extraUserPrice: stripeTier.extra_user_price || defaultTier.extraUserPrice,
              extraOperatorPrice: stripeTier.extra_operator_price || defaultTier.extraOperatorPrice,
            };
            return { ...merged, features: buildFeatures(merged) };
          }
          return { ...defaultTier, features: buildFeatures(defaultTier) };
        });
        setTiers(dynamicTiers);
      } catch (error) {
        console.error('Failed to fetch tier pricing:', error);
        setPricingAvailable(false);
        setTiers(DEFAULT_TIERS.map(t => ({ ...t, features: buildFeatures(t) })));
      } finally {
        setTiersLoading(false);
      }
    };
    fetchTierPricing();
  }, []);

  const passwordChecks = {
    length: form.password.length >= 8,
    number: /\d/.test(form.password),
  };
  const passwordValid = passwordChecks.length && passwordChecks.number;
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateDetails = () => {
    const e = {};
    if (!form.companyName.trim()) e.companyName = 'Company name is required';
    if (!form.industryType) e.industryType = 'Please select your industry';
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.workEmail.trim()) e.workEmail = 'Work email is required';
    else if (!isValidEmail(form.workEmail)) e.workEmail = 'Please enter a valid email address';
    if (!form.password) e.password = 'Password is required';
    else if (!passwordValid) e.password = 'Password does not meet requirements';
    if (!form.termsAccepted) e.terms = 'You must accept the Terms & Conditions';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSelectTier = (tier) => {
    setSelectedTier(tier.id);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateDetails()) return;

    setLoading(true);
    const email = form.workEmail.trim().toLowerCase();
    const password = form.password;
    try {
      await axios.post(`${API_URL}/trial/register`, {
        tier: selectedTier,
        company_name: form.companyName.trim(),
        industry_type: form.industryType,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email,
        phone: form.phone.trim() || null,
        password,
        terms_accepted: form.termsAccepted,
      });

      toast.success('Account created!', { description: 'Setting up your workspace…' });
      localStorage.setItem('show_onboarding_wizard', 'true');
      try {
        await loginV2(email, password);
        window.location.href = '/onboarding';
      } catch (loginErr) {
        toast.info('Account created. Please log in to continue.');
        navigate('/login');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Failed to create your account. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlanName = DEFAULT_TIERS.find((t) => t.id === selectedTier)?.name || '';

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col items-center py-10 px-4">
      {/* Subtle texture overlay */}
      <div
        className="fixed inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* ===================== STEP 1 — Choose your plan ===================== */}
      {step === 1 && (
        <div className="w-full max-w-4xl relative z-10" data-testid="plan-selection">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
              </div>
            </div>
            <h1 className="text-3xl font-heading font-bold text-white mb-2">Choose Your Plan</h1>
            <p className="text-zinc-400">Select the plan that best fits your business needs</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full">
              <Gift className="h-5 w-5" />
              <span className="font-medium">{trialPeriodDays}-day free trial · No credit card required</span>
            </div>
          </div>

          {!pricingAvailable && !tiersLoading && (
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
              <p className="text-amber-400 font-medium">Live pricing is currently unavailable</p>
              <p className="text-amber-400/70 text-sm mt-1">You can still start your free trial — billing details are collected at the end of your trial.</p>
            </div>
          )}

          {tiersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {tiers.map((tier) => {
                const isProfessional = tier.id === 'professional';
                return (
                  <Card
                    key={tier.id}
                    data-testid={`plan-card-${tier.id}`}
                    className={`relative border-2 transition-all flex flex-col ${
                      isProfessional ? 'cursor-default' : 'cursor-pointer hover:border-[#ffd108]'
                    } ${tier.popular ? 'border-[#ffd108]' : 'border-zinc-800'}`}
                    onClick={() => !isProfessional && handleSelectTier(tier)}
                  >
                    {tier.popular && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ffd108] text-black">
                        Most Popular
                      </Badge>
                    )}
                    <CardHeader className="text-center pb-2">
                      <CardTitle className="font-heading text-xl">{tier.name}</CardTitle>
                      <div className="mt-2">
                        {isProfessional ? (
                          <>
                            <span className="text-2xl font-bold text-[#ffd108]">Custom Pricing</span>
                            <p className="text-sm text-muted-foreground mt-1">Tailored to your needs</p>
                          </>
                        ) : (
                          <>
                            <span className="text-4xl font-bold">${tier.price}</span>
                            <span className="text-muted-foreground">/month {currency !== 'AUD' && `(${currency})`}</span>
                          </>
                        )}
                      </div>
                      <CardDescription className="mt-2">{tier.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <ul className="space-y-2 flex-1">
                        {tier.features.map((feature, idx) => (
                          <li key={`feature-${idx}`} className="flex items-start gap-2 text-sm">
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {isProfessional ? (
                        <a
                          href="https://www.equiprsoftware.com/book-a-demo"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mt-6"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            className="w-full"
                            variant="outline"
                            data-testid="plan-request-demo"
                          >
                            REQUEST A DEMO
                          </Button>
                        </a>
                      ) : (
                        <Button
                          className={`w-full mt-6 ${tier.popular ? 'btn-volt' : ''}`}
                          variant={tier.popular ? 'default' : 'outline'}
                          data-testid={`plan-select-${tier.id}`}
                        >
                          SELECT {tier.name.toUpperCase()}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Feature Comparison Matrix */}
          {!tiersLoading && <FeatureMatrix />}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-zinc-400 hover:text-white transition-colors inline-flex items-center"
            >
              <ArrowLeft className="h-4 w-4 inline mr-1" />
              Back to login
            </Link>
          </div>
        </div>
      )}

      {/* ===================== STEP 2 — Business & admin details ===================== */}
      {step === 2 && (
        <div className="w-full max-w-2xl relative z-10">
          <div className="mb-8">
            <div className="w-12 h-12 rounded-xl overflow-hidden mb-4">
              <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
            </div>
            <div className="inline-flex items-center gap-2 bg-[#ffd108]/10 text-[#ffd108] px-3 py-1.5 rounded-full border border-[#ffd108]/20 mb-3">
              <Gift className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">{trialPeriodDays}-day free trial · No credit card required</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white">Tell us about your business</h1>
            <p className="text-zinc-400 text-sm mt-2">
              You're starting a free trial on the {selectedPlanName} plan.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-details-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="companyName" className="text-zinc-300 text-sm">Company Name</Label>
                <Input
                  id="companyName"
                  data-testid="signup-company-name"
                  value={form.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="Acme Cranes Pty Ltd"
                  className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] ${errors.companyName ? 'border-red-500' : ''}`}
                />
                {errors.companyName && <p className="text-red-500 text-xs">{errors.companyName}</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-300 text-sm">Industry</Label>
                <Select value={form.industryType} onValueChange={(v) => handleInputChange('industryType', v)}>
                  <SelectTrigger
                    data-testid="signup-industry"
                    className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] ${errors.industryType ? 'border-red-500' : ''}`}
                  >
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => (
                      <SelectItem key={ind.value} value={ind.value} data-testid={`industry-option-${ind.value}`}>
                        {ind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.industryType && <p className="text-red-500 text-xs">{errors.industryType}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="firstName" className="text-zinc-300 text-sm">First Name</Label>
                <Input
                  id="firstName"
                  data-testid="signup-first-name"
                  value={form.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="John"
                  className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] ${errors.firstName ? 'border-red-500' : ''}`}
                />
                {errors.firstName && <p className="text-red-500 text-xs">{errors.firstName}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="lastName" className="text-zinc-300 text-sm">Last Name</Label>
                <Input
                  id="lastName"
                  data-testid="signup-last-name"
                  value={form.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Smith"
                  className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] ${errors.lastName ? 'border-red-500' : ''}`}
                />
                {errors.lastName && <p className="text-red-500 text-xs">{errors.lastName}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="workEmail" className="text-zinc-300 text-sm">Work Email</Label>
                <Input
                  id="workEmail"
                  type="email"
                  data-testid="signup-work-email"
                  value={form.workEmail}
                  onChange={(e) => handleInputChange('workEmail', e.target.value)}
                  placeholder="john@yourcompany.com"
                  className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] ${errors.workEmail ? 'border-red-500' : ''}`}
                />
                {errors.workEmail && <p className="text-red-500 text-xs">{errors.workEmail}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="phone" className="text-zinc-300 text-sm">Phone <span className="text-zinc-600">(optional)</span></Label>
                <Input
                  id="phone"
                  data-testid="signup-phone"
                  value={form.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="0400 000 000"
                  className="bg-zinc-900 border-zinc-800 focus:border-[#ffd108]"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="text-zinc-300 text-sm">Create a Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="signup-password"
                  value={form.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Min 8 characters"
                  className={`bg-zinc-900 border-zinc-800 focus:border-[#ffd108] pr-10 ${errors.password ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {form.password && (
                <div className="flex gap-4 mt-1">
                  <div className={`flex items-center gap-1 text-xs ${passwordChecks.length ? 'text-green-500' : 'text-zinc-500'}`}>
                    <Check className={`h-3 w-3 ${passwordChecks.length ? '' : 'opacity-50'}`} /> 8+ characters
                  </div>
                  <div className={`flex items-center gap-1 text-xs ${passwordChecks.number ? 'text-green-500' : 'text-zinc-500'}`}>
                    <Check className={`h-3 w-3 ${passwordChecks.number ? '' : 'opacity-50'}`} /> 1 number
                  </div>
                </div>
              )}
              {errors.password && !form.password && <p className="text-red-500 text-xs">{errors.password}</p>}
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                data-testid="signup-terms"
                checked={form.termsAccepted}
                onCheckedChange={(v) => handleInputChange('termsAccepted', v === true)}
                className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#ffd108] data-[state=checked]:border-[#ffd108] data-[state=checked]:text-zinc-900"
              />
              <Label htmlFor="terms" className="text-zinc-400 text-sm leading-relaxed cursor-pointer">
                I agree to the{' '}
                <Link to="/eula" target="_blank" className="text-[#ffd108] hover:underline">Terms of Service</Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" className="text-[#ffd108] hover:underline">Privacy Policy</Link>.
              </Label>
            </div>
            {errors.terms && <p className="text-red-500 text-xs">{errors.terms}</p>}

            <div className="flex items-center gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(1)}
                disabled={loading}
                className="text-zinc-400 hover:text-white"
                data-testid="signup-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button type="submit" className="flex-1 btn-volt py-5" disabled={loading} data-testid="signup-submit-btn">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating your account…
                  </>
                ) : (
                  <>
                    Start my free trial <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <p className="text-zinc-500 text-sm pt-2">
              Already have an account?{' '}
              <Link to="/login" className="text-[#ffd108] hover:underline">Log In</Link>
            </p>
          </form>
        </div>
      )}

      {/* Professional plan quote dialog */}
      <RequestQuoteDialog open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default FreeTrial;
